import React, { useEffect, useState } from 'react';
import { getComplain } from '../../api/complain';

function ComplainList() {
  const [complainData, setComplainData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [resolutionNote, setResolutionNote] = useState('');

  function getAllComplain() {
    setLoading(true);
    getComplain((result) => {
      if (result.ok !== 1) {
        console.log(result);
        setLoading(false);
        return;
      }
      setComplainData(result.result);
      setLoading(false);
    });
  }

  useEffect(() => {
    getAllComplain();
  }, []);

  // Function to format date and remove time - handles ISO format
  const formatDateOnly = (dateTimeString) => {
    if (!dateTimeString) return '';
    
    try {
      // Handle ISO format like "2025-10-02T00:00:00.000Z"
      if (dateTimeString.includes('T')) {
        const datePart = dateTimeString.split('T')[0];
        return datePart;
      }
      
      // Handle space-separated format like "2025-10-02 14:30:22"
      if (dateTimeString.includes(' ')) {
        const datePart = dateTimeString.split(' ')[0];
        return datePart;
      }
      
      // If it's already just a date, return as is
      return dateTimeString;
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateTimeString;
    }
  };

  const filteredData = complainData.filter(item => {
    const matchesSearch = item.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.mobileNumber?.includes(searchTerm) ||
                         item.complain?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || 
                         (filterStatus === 'resolved' && item.status === 'Resolved') ||
                         (filterStatus === 'unresolved' && item.status === 'Un-resolved');
    return matchesSearch && matchesFilter;
  });

  const unresolvedCount = complainData.filter(item => item.status === 'Un-resolved').length;
  const resolvedCount = complainData.filter(item => item.status === 'Resolved').length;

  const handleResolve = (item) => {
    setSelectedComplaint(item);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedComplaint(null);
    setResolutionNote('');
  };

  const handleSubmitResolution = () => {
    console.log('Resolving complaint:', selectedComplaint);
    console.log('Resolution note:', resolutionNote);
    
    // Add your API call here to update the complaint status
    // Example:
    // updateComplaintStatus(selectedComplaint.id, {
    //   status: 'Resolved',
    //   resolutionNote: resolutionNote
    // });
    
    handleCloseModal();
    getAllComplain(); // Refresh the list
  };

  const ComplaintCard = ({ item, index }) => (
    <div className="card border-0 shadow-sm mb-3">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div className="d-flex align-items-center">
            <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-3" style={{width: '40px', height: '40px'}}>
              <i className="bi bi-person-fill text-primary"></i>
            </div>
            <div>
              <h6 className="mb-1 fw-bold">{item.fullName}</h6>
              <small className="text-muted">
                <i className="bi bi-telephone me-1"></i>
                {item.mobileNumber}
              </small>
            </div>
          </div>
          <div className="text-end">
            {item.status === 'Un-resolved' ? (
              <span className="badge bg-danger">
                <i className="bi bi-x-circle me-1"></i>
                Unresolved
              </span>
            ) : (
              <span className="badge bg-success">
                <i className="bi bi-check-circle me-1"></i>
                Resolved
              </span>
            )}
          </div>
        </div>

        <div className="row g-2 mb-3">
          <div className="col-6">
            <small className="text-muted d-block">Border</small>
            <span className="badge bg-info bg-opacity-10 text-info">{item.borderName}</span>
          </div>
          <div className="col-6">
            <small className="text-muted d-block">Date</small>
            <small className="fw-medium">{formatDateOnly(item.date_time)}</small>
          </div>
          <div className="col-6">
            <small className="text-muted d-block">Gender</small>
            <small className="fw-medium">{item.gender}</small>
          </div>
          <div className="col-6">
            <small className="text-muted d-block">Age</small>
            <small className="fw-medium">{item.age}</small>
          </div>
        </div>

        <div className="mb-3">
          <small className="text-muted d-block mb-1">Complaint</small>
          <p className="mb-0 small">{item.complain}</p>
        </div>

        {item.status === 'Un-resolved' && (
          <div className="d-grid">
            <button
              onClick={() => handleResolve(item)}
              className="btn btn-primary btn-sm"
            >
              <i className="bi bi-check2 me-1"></i>
              Resolve Complaint
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="container-fluid px-3 px-md-4 py-4" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      <div className="row">
        <div className="col-12">
          {/* Header */}
          <div className="mb-4">
            <h2 className="mb-1 fw-bold">Complaint Management</h2>
            <p className="text-muted">Track and manage all customer complaints</p>
          </div>

          {/* Stats Cards */}
          <div className="row g-3 mb-4">
            <div className="col-12 col-sm-6 col-lg-4">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <p className="text-muted mb-1 small">Total Complaints</p>
                      <h3 className="mb-0 fw-bold">{complainData.length}</h3>
                    </div>
                    <div className="bg-primary bg-opacity-10 p-3 rounded">
                      <i className="bi bi-exclamation-circle fs-4 text-primary"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-sm-6 col-lg-4">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <p className="text-muted mb-1 small">Unresolved</p>
                      <h3 className="mb-0 fw-bold text-danger">{unresolvedCount}</h3>
                    </div>
                    <div className="bg-danger bg-opacity-10 p-3 rounded">
                      <i className="bi bi-x-circle fs-4 text-danger"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-sm-6 col-lg-4">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <p className="text-muted mb-1 small">Resolved</p>
                      <h3 className="mb-0 fw-bold text-success">{resolvedCount}</h3>
                    </div>
                    <div className="bg-success bg-opacity-10 p-3 rounded">
                      <i className="bi bi-check-circle fs-4 text-success"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Filters and Search */}
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-body">
              <div className="row g-3">
                <div className="col-12">
                  <div className="input-group">
                    <span className="input-group-text bg-white border-end-0">
                      <i className="bi bi-search text-muted"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control border-start-0 ps-0"
                      placeholder="Search by name, mobile, or complaint..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>

                <div className="col-12">
                  <div className="d-flex gap-2 flex-wrap align-items-center">
                    <button
                      onClick={() => setFilterStatus('all')}
                      className={`btn btn-sm ${filterStatus === 'all' ? 'btn-primary' : 'btn-outline-secondary'}`}
                    >
                      All
                    </button>
                    <button
                      onClick={() => setFilterStatus('unresolved')}
                      className={`btn btn-sm ${filterStatus === 'unresolved' ? 'btn-danger' : 'btn-outline-secondary'}`}
                    >
                      Unresolved
                    </button>
                    <button
                      onClick={() => setFilterStatus('resolved')}
                      className={`btn btn-sm ${filterStatus === 'resolved' ? 'btn-success' : 'btn-outline-secondary'}`}
                    >
                      Resolved
                    </button>
                    <button
                      onClick={getAllComplain}
                      disabled={loading}
                      className="btn btn-sm btn-outline-secondary ms-auto"
                      title="Refresh"
                    >
                      <i className={`bi bi-arrow-clockwise ${loading ? 'spinner-border spinner-border-sm' : ''}`}></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : filteredData.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-inbox fs-1 text-muted"></i>
              <p className="text-muted mt-2">No complaints found</p>
            </div>
          ) : (
            <>
              {/* Mobile Card View (visible on small screens) */}
              <div className="d-block d-lg-none">
                {filteredData.map((item, index) => (
                  <ComplaintCard key={index} item={item} index={index} />
                ))}
              </div>

              {/* Desktop Table View (visible on large screens) */}
              <div className="card border-0 shadow-sm d-none d-lg-block">
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th className="fw-semibold text-uppercase small border-0" style={{minWidth: '100px'}}>Date</th>
                          <th className="fw-semibold text-uppercase small border-0" style={{minWidth: '200px'}}>Complainant</th>
                          <th className="fw-semibold text-uppercase small border-0" style={{minWidth: '120px'}}>Mobile No</th>
                          <th className="fw-semibold text-uppercase small border-0" style={{minWidth: '100px'}}>Border</th>
                          <th className="fw-semibold text-uppercase small border-0" style={{minWidth: '250px'}}>Complaint</th>
                          <th className="fw-semibold text-uppercase small border-0" style={{minWidth: '100px'}}>Status</th>
                          <th className="fw-semibold text-uppercase small border-0" style={{minWidth: '100px'}}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredData.map((item, index) => (
                          <tr key={index}>
                            <td className="text-nowrap">
                              <small className="text-muted">
                                <i className="bi bi-calendar3 me-1"></i>
                                {formatDateOnly(item.date_time)}
                              </small>
                            </td>
                            <td>
                              <div className="d-flex align-items-center">
                                <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-2" style={{width: '40px', height: '40px', flexShrink: 0}}>
                                  <i className="bi bi-person-fill text-primary"></i>
                                </div>
                                <div className="flex-grow-1 min-width-0">
                                  <div className="fw-medium text-truncate">{item.fullName}</div>
                                  <small className="text-muted d-block">Gender: {item.gender}</small>
                                  <small className="text-muted d-block">Age: {item.age}</small>
                                </div>
                              </div>
                            </td>
                            <td className="text-nowrap">
                              <small>
                                <i className="bi bi-telephone me-1 text-muted"></i>
                                {item.mobileNumber}
                              </small>
                            </td>
                            <td>
                              <span className="badge bg-info bg-opacity-10 text-info">
                                {item.borderName}
                              </span>
                            </td>
                            <td>
                              <div className="text-wrap" style={{maxWidth: '300px', wordBreak: 'break-word'}}>
                                <span title={item.complain}>
                                  {item.complain}
                                </span>
                              </div>
                            </td>
                            <td>
                              {item.status === 'Un-resolved' ? (
                                <span className="badge bg-danger">
                                  <i className="bi bi-x-circle me-1"></i>
                                  Unresolved
                                </span>
                              ) : (
                                <span className="badge bg-success">
                                  <i className="bi bi-check-circle me-1"></i>
                                  Resolved
                                </span>
                              )}
                            </td>
                            <td>
                              {item.status === 'Un-resolved' && (
                                <button
                                  onClick={() => handleResolve(item)}
                                  className="btn btn-sm btn-primary"
                                >
                                  <i className="bi bi-check2 me-1"></i>
                                  Resolve
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Footer */}
          <div className="mt-3 text-center text-muted small">
            Showing {filteredData.length} of {complainData.length} complaints
          </div>
        </div>
      </div>

      {/* Resolution Modal */}
      {showModal && (
        <>
          <div className="modal fade show d-block" tabIndex="-1" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content">
                <div className="modal-header border-0 pb-0">
                  <h5 className="modal-title fw-bold">Resolve Complaint</h5>
                  <button type="button" className="btn-close" onClick={handleCloseModal}></button>
                </div>
                <div className="modal-body">
                  {selectedComplaint && (
                    <>
                      <div className="mb-4">
                        <h6 className="text-muted small mb-3">COMPLAINT DETAILS</h6>
                        <div className="card bg-light border-0">
                          <div className="card-body">
                            <div className="row g-3">
                              <div className="col-12 col-sm-6">
                                <small className="text-muted d-block">Complainant</small>
                                <strong>{selectedComplaint.fullName}</strong>
                              </div>
                              <div className="col-12 col-sm-6">
                                <small className="text-muted d-block">Mobile</small>
                                <strong>{selectedComplaint.mobileNumber}</strong>
                              </div>
                              <div className="col-12 col-sm-6">
                                <small className="text-muted d-block">Border</small>
                                <span className="badge bg-info">{selectedComplaint.borderName}</span>
                              </div>
                              <div className="col-12 col-sm-6">
                                <small className="text-muted d-block">Date</small>
                                <strong>{formatDateOnly(selectedComplaint.date_time)}</strong>
                              </div>
                              <div className="col-12 col-sm-6">
                                <small className="text-muted d-block">Gender</small>
                                <strong>{selectedComplaint.gender}</strong>
                              </div>
                              <div className="col-12 col-sm-6">
                                <small className="text-muted d-block">Age</small>
                                <strong>{selectedComplaint.age}</strong>
                              </div>
                              <div className="col-12">
                                <small className="text-muted d-block mb-1">Complaint</small>
                                <p className="mb-0">{selectedComplaint.complain}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="mb-3">
                        <label htmlFor="resolutionNote" className="form-label fw-semibold">
                          Resolution Notes <span className="text-danger">*</span>
                        </label>
                        <textarea
                          id="resolutionNote"
                          className="form-control"
                          rows="4"
                          placeholder="Enter details about how this complaint was resolved..."
                          value={resolutionNote}
                          onChange={(e) => setResolutionNote(e.target.value)}
                        ></textarea>
                        <small className="text-muted">Provide a detailed explanation of the resolution</small>
                      </div>
                    </>
                  )}
                </div>
                <div className="modal-footer border-0 pt-0 flex-column flex-sm-row">
                  <button type="button" className="btn btn-secondary w-100 w-sm-auto mb-2 mb-sm-0 me-sm-2" onClick={handleCloseModal}>
                    Cancel
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-success w-100 w-sm-auto"
                    onClick={handleSubmitResolution}
                    disabled={!resolutionNote.trim()}
                  >
                    <i className="bi bi-check-circle me-2"></i>
                    Mark as Resolved
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default ComplainList;